/**
 * Dialog for duplicating files with path validation
 */
import { useState, useEffect } from 'react';
import { Copy, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import type { File } from '@phrasewright/types';
import { useAppSelector } from '@/store/hooks';

interface DuplicateFileDialogProps {
  open: boolean;
  onClose: () => void;
  onDuplicate: (newPath: string, newName: string) => Promise<void>;
  originalFile: File;
}

export function DuplicateFileDialog({
  open,
  onClose,
  onDuplicate,
  originalFile,
}: DuplicateFileDialogProps) {
  const { files } = useAppSelector((state) => state.files);
  const [newPath, setNewPath] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Generate default duplicate path
  useEffect(() => {
    if (open && originalFile) {
      const filePath = originalFile.path || originalFile.name;
      const pathWithoutExt = filePath.replace(/\.[^/.]+$/, '');
      const extension = filePath.match(/\.[^/.]+$/)?.[0] || '';

      // Find next available copy number
      let copyNumber = 1;
      let candidatePath = `${pathWithoutExt}.copy${extension}`;

      while (files.some((f) => f.path === candidatePath)) {
        copyNumber++;
        candidatePath = `${pathWithoutExt}.copy${copyNumber}${extension}`;
      }

      setNewPath(candidatePath);
      setError('');
    }
  }, [open, originalFile, files]);

  const newName = newPath.split('/').pop() || '';

  // Validate the new path
  const validatePath = (path: string): string | null => {
    if (!path.trim()) {
      return 'Path cannot be empty';
    }

    if (path === (originalFile.path || originalFile.name)) {
      return 'New path must be different from original';
    }

    if (files.some((f) => f.path === path)) {
      return 'A file with this path already exists';
    }

    if (!/^[a-zA-Z0-9._/-]+$/.test(path)) {
      return 'Path contains invalid characters';
    }

    if (!path.endsWith('.md') && !path.endsWith('.txt')) {
      return 'File must have .md or .txt extension';
    }

    return null;
  };

  const handlePathChange = (value: string) => {
    setNewPath(value);
    const validationError = validatePath(value);
    setError(validationError || '');
  };

  const handleDuplicate = async () => {
    const validationError = validatePath(newPath);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onDuplicate(newPath, newName);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError('');
      onClose();
    }
  };

  const isValid = !error && newPath.trim();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-4 w-4" />
            Duplicate File
          </DialogTitle>
          <DialogDescription>
            Create a copy of "{originalFile?.name}" with a new path.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Original file info */}
          <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
            <div className="flex-1">
              <div className="font-medium text-sm">{originalFile?.name}</div>
              <div className="text-xs text-muted-foreground">
                {originalFile?.path}
              </div>
            </div>
            <Badge variant="secondary">{originalFile?.metadata.type}</Badge>
          </div>

          {/* New path input */}
          <div className="space-y-2">
            <label htmlFor="newPath" className="text-sm font-medium">
              New file path
            </label>
            <Input
              id="newPath"
              value={newPath}
              onChange={(e) => handlePathChange(e.target.value)}
              placeholder="Enter new file path..."
              disabled={isLoading}
            />
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
          </div>

          {/* Preview */}
          {newName && !error && (
            <div className="p-3 bg-green-50 rounded-md border">
              <div className="text-sm font-medium text-green-900">Preview</div>
              <div className="text-xs text-green-700 mt-1">
                Name: {newName}
                <br />
                Path: {newPath}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDuplicate}
            disabled={!isValid || isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Duplicating...
              </div>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
